import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useUserApi } from "../api/UserApi";
import { toast } from "react-toastify";
import { useUser } from "../context/UserContext";
import { useFontenehus } from "../context/FontenehusContext";
import { handleApiError } from "../utils/handleApiError";

const LoginPage: React.FC = () => {
  const navigate = useNavigate();
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [showResend, setShowResend] = useState(false);
  const { loginUser, resendVerification } = useUserApi();
  const { setUser } = useUser();
  const { setSelectedFontenehus, refreshFontenehus } = useFontenehus();
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError("");
    setShowResend(false);
    setLoading(true);

    try {
      const response = await loginUser(username, password);
      const responseData = response.data.data;

      setUser(responseData.user);
      console.log(responseData.user);
      localStorage.setItem("jwtToken", responseData.token);
      localStorage.setItem("user", JSON.stringify(responseData.user));
      setSelectedFontenehus({
        id: responseData.user.fontenehusId,
        name: responseData.user.fontenehusName,
        fontenehusCode: responseData.user.fontenhusCode,
      }); // Reset selected Fontenehus on login
      navigate("/main");
    } catch (err: any) {
      console.error("Login error:", err);

      // Axios error detection
      if (err.response) {
        if (
          err.response.status === 403 &&
          err.response.data.message?.includes("ikke verifisert")
        ) {
          setError("Du må verifisere e-posten din før du kan logge inn.");
          setShowResend(true); // 👈 show resend link
        } else {
          setError(
            err.response.data.message || "Feil brukernavn eller passord.",
          );
        }
      } else {
        setError("Kunne ikke koble til serveren. Prøv igjen senere.");
      }
    } finally {
      refreshFontenehus();
      setLoading(false);
    }
  };

  const handleResend = async () => {
    try {
      setShowResend(false);
      await resendVerification(username);
      toast.success("Verifikasjonslenke sendt på nytt!");
    } catch (err: any) {
      handleApiError(err, "Kunne ikke sende ny e-post. Prøv igjen senere.");
    }
  };

  return (
    <div className="relative flex flex-col items-center justify-center min-h-screen bg-orange-50 overflow-hidden">
      {/* === HEADER with logo === */}
      <header className="absolute top-0 left-0 w-full flex justify-center py-4 bg-white/80 backdrop-blur-sm shadow-sm z-30">
        <img
          src="/assets/fontenehusetlogo.webp"
          alt="Fontenehuset Logo"
          className="w-40 sm:w-48 md:w-56 opacity-95 drop-shadow-md"
        />
      </header>

      {/* === Centered content === */}
      <div className="flex flex-col items-center justify-center translate-y-[-10%]">
        {/* Login Card */}
        <div className="relative z-20 bg-white/95 backdrop-blur-sm rounded-2xl shadow-lg p-8 w-full max-w-md mx-4 border border-orangeBrand/20">
          <h1 className="text-3xl font-bold text-center text-orangeBrand mb-2">
            🍲 FonteneLunsj
          </h1>
          <p className="text-center text-textDark/70 mb-6">
            Logg inn for å se dagens oppskrifter
          </p>

          <form onSubmit={handleSubmit} className="flex flex-col gap-4">
            <div>
              <label className="block text-textDark font-medium mb-1">
                Brukernavn
              </label>
              <input
                type="text"
                value={username}
                onChange={(e) => setUsername(e.target.value)}
                className="w-full border border-orangeBrand/30 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-orangeBrand"
                placeholder="Skriv inn brukernavn"
                required
              />
            </div>

            <div>
              <label className="block text-textDark font-medium mb-1">
                Passord
              </label>
              <input
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="w-full border border-orangeBrand/30 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-orangeBrand"
                placeholder="••••••••"
                required
              />
            </div>

            {error && (
              <p className="text-red-500 text-sm text-center">{error}</p>
            )}

            {/* 👇 Show resend verification button */}
            {showResend && (
              <div className="text-center mt-2">
                <button
                  type="button"
                  onClick={handleResend}
                  className="text-orangeBrand hover:text-orangeHover font-medium underline"
                >
                  Send verifikasjonslenke på nytt
                </button>
              </div>
            )}

            <button
              type="submit"
              disabled={loading}
              className="bg-orangeBrand hover:bg-orangeHover text-white font-semibold py-2 rounded-lg transition mt-2"
            >
              {loading ? "Logger inn..." : "Logg inn"}
            </button>
          </form>

          <div className="mt-4 text-center text-sm text-textDark/80">
            <p>
              Har du ikke bruker?{" "}
              <button
                type="button"
                className="text-orangeBrand hover:text-orangeHover font-medium"
                onClick={() => {
                  refreshFontenehus();
                  navigate("/registrer");
                }}
              >
                Registrer deg
              </button>
            </p>
            <button
              type="button"
              className="text-orangeBrand hover:text-orangeHover mt-2"
              onClick={() => navigate("/glemt-passord")}
            >
              Glemt passord?
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default LoginPage;
